import {
  useState, useCallback, useMemo, createContext, useContext,
} from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { HiOutlineChevronRight, HiOutlineFlag } from 'react-icons/hi2';
import {
  RunHealthStatus,
  RunHealthObj,
  AntSensorHealth,
  GpsHealth,
  ImuSensorHealth,
  GateHealth,
  RouteHealth,
  DiffSensorStatus,
  DiffChannelHealth,
  WindSensorHealth,
  SensorInfoObj,
  ProcessingStage,
  getStageName,
  SensorOffsetsScope,
  WindSensor5ZeroOverride,
  DynamicPressureSelection,
} from 'types/Run';
import { patchRun, patchRunsBulk } from 'api/runs';
import { isSentinelRunTs } from 'utils/sessionTime';
import WeatherSourceBadge from 'components/WeatherSourceBadge';
import SyntheticDpBadge from 'components/SyntheticDpBadge';
import ForceSyntheticDpControl from 'components/ForceSyntheticDpControl';
import { withForceSyntheticDp } from 'utils/forceSyntheticDp';
import { sensorIdentity, sensorPositionLabel } from '../../CalibrationView/groundSpeedUtils';
import {
  roleFor, SensorRole, SensorRoleCalibration, sensorRoles,
} from './sensorRoles';

interface RunHealthCheckContext {
  run?: any;
  calibration?: SensorRoleCalibration | null;
  sessionData?: any;
}

// --- Helpers ---

function healthStatusColor(status: number | null | undefined): string {
  switch (status) {
    case RunHealthStatus.GOOD:
      return 'cr-bg-green-500';
    case RunHealthStatus.AVERAGE:
      return 'cr-bg-yellow-500';
    case RunHealthStatus.WARNING:
      return 'cr-bg-orange-500';
    case RunHealthStatus.ERROR:
      return 'cr-bg-red-500';
    default:
      return 'cr-bg-gray-400';
  }
}

function healthStatusTextColor(status: number | null | undefined): string {
  switch (status) {
    case RunHealthStatus.GOOD:
      return 'cr-text-green-700';
    case RunHealthStatus.AVERAGE:
      return 'cr-text-yellow-700';
    case RunHealthStatus.WARNING:
      return 'cr-text-orange-700';
    case RunHealthStatus.ERROR:
      return 'cr-text-red-700';
    default:
      return 'cr-text-gray-500';
  }
}

function presenceDot(present: boolean) {
  return (
    <span
      className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${present ? 'cr-bg-green-500' : 'cr-bg-red-400'}`}
      title={present ? 'Present' : 'Not present'}
    />
  );
}

function formatPercent(fraction: number | null | undefined): string {
  if (fraction == null) return '--';
  return `${(fraction * 100).toFixed(0)}%`;
}

function formatTime(seconds: number | null | undefined): string {
  if (seconds == null) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m${secs.toString().padStart(2, '0')}s`;
}

function formatNumber(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '--';
  return value.toFixed(decimals);
}

// --- Sub-components ---

function HealthStatusBadge({ healthObj, dp }: { healthObj: RunHealthObj; dp?: DynamicPressureSelection | null }) {
  const status = healthObj.health_status;
  // Title-case the enum name (e.g. "WARNING" -> "Warning")
  const raw = healthObj.health_status_name ?? '';
  const name = raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : '';
  return (
    <div className="cr-flex cr-items-center cr-gap-3">
      <span className={`cr-inline-block cr-h-4 cr-w-4 cr-rounded-full ${healthStatusColor(status)}`} />
      <span className={`cr-text-lg cr-font-bold ${healthStatusTextColor(status)}`}>{name}</span>
      <WeatherSourceBadge weather={healthObj.weather} />
      <SyntheticDpBadge dp={dp} />
    </div>
  );
}

// Section-level expand state. A single chevron in each section header
// (ANT / Other) toggles every card in that section at once, so the whole row
// reveals its details together instead of one card stretching the others.
const SectionExpandedContext = createContext(false);

function SectionHeader({ title, expanded, onToggle }: { title: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="cr-mb-2 cr-flex cr-items-center cr-gap-1.5 cr-text-xs cr-font-semibold cr-uppercase
        cr-tracking-wide cr-text-gray-400 hover:cr-text-gray-600"
    >
      <HiOutlineChevronRight className={`cr-h-3.5 cr-w-3.5 cr-transition-transform ${expanded ? 'cr-rotate-90' : ''}`} />
      {title}
    </button>
  );
}

function ExpandableCard({
  title,
  statusColor,
  statusLabel,
  summary,
  detail,
  children,
}: {
  title: string;
  statusColor: string;
  statusLabel: string;
  summary?: string;
  detail?: string;
  children?: React.ReactNode;
}) {
  const expanded = useContext(SectionExpandedContext);

  return (
    <div className="cr-flex cr-w-full cr-flex-col cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md md:cr-w-56">
      <span className="font-montserrat cr-mb-2 cr-text-sm cr-font-bold cr-uppercase cr-text-blue-900">{title}</span>
      <div className="cr-flex cr-items-start cr-gap-2 cr-text-sm cr-text-blue-900">
        <span className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-mt-1 cr-rounded-full ${statusColor}`} />
        <span>{statusLabel}</span>
      </div>
      {summary && <div className="cr-mt-1 cr-text-xs cr-text-gray-500">{summary}</div>}
      {detail && <div className="cr-text-xs cr-font-medium cr-text-gray-600">{detail}</div>}
      {expanded && children && <div className="cr-mt-3 cr-border-t cr-pt-3 cr-text-xs cr-text-blue-900">{children}</div>}
    </div>
  );
}

function SensorInfoSection({ sensorInfo, showAntId = true }: { sensorInfo?: SensorInfoObj; showAntId?: boolean }) {
  if (!sensorInfo) return null;
  const { ant_device_number, ant_id_not_found_reason, battery_voltage, firmware_version } = sensorInfo;
  const hasAntId = showAntId && (ant_device_number != null || ant_id_not_found_reason != null);
  if (!hasAntId && battery_voltage == null && firmware_version == null) return null;
  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2 cr-space-y-1">
      {showAntId && ant_device_number != null && <div>ANT ID: {ant_device_number}</div>}
      {showAntId && ant_device_number == null && ant_id_not_found_reason && (
        <div className="cr-text-orange-500">ANT ID: <span className="cr-italic">{ant_id_not_found_reason}</span></div>
      )}
      {battery_voltage != null && <div>Battery: {battery_voltage}V</div>}
      {firmware_version != null && <div>Firmware: v{firmware_version}</div>}
    </div>
  );
}

const ANT_PACKET_DROP_TOLERANCE = 0.15;

// The card's second line: which of this function's sensors this one is, and
// which wheel it is on. `showPrimary` is the "more than one" test — with a
// single power meter there is nothing for it to be primary *over*, so saying
// so is noise. Position stands on its own and is shown whenever it is known.
function roleDetail(role: SensorRole | undefined, showPrimary: boolean): string | undefined {
  const parts: string[] = [];
  if (showPrimary && role?.primary != null) parts.push(role.primary ? 'Primary' : 'Auxiliary');
  const position = sensorPositionLabel(role?.position);
  if (position) parts.push(position);
  return parts.length ? parts.join(' · ') : undefined;
}

function AntSensorCard({
  title,
  sensor,
  sensorInfo,
  role,
  showPrimary = false,
  children,
  statusColor,
  healthLabel,
  isOptional = false,
}: {
  title: string;
  sensor: AntSensorHealth;
  sensorInfo?: SensorInfoObj;
  role?: SensorRole;
  showPrimary?: boolean;
  children?: React.ReactNode;
  statusColor?: string;
  healthLabel?: string;
  isOptional?: boolean;
}) {
  const antIdSummary = sensorInfo?.ant_device_number != null
    ? `ANT ID: ${sensorInfo.ant_device_number}`
    : sensorInfo?.ant_id_not_found_reason
      ? `ANT ID: ${sensorInfo.ant_id_not_found_reason}`
      : null;
  const dropFrac = sensor.packets_dropped_fraction;
  const droppedHigh = sensor.present && dropFrac != null && dropFrac > ANT_PACKET_DROP_TOLERANCE;
  // A sensor that paired but logged zero packets reads "Missing", not "Good".
  const hasData = sensor.has_data ?? ((sensor.raw?.n_samples ?? 0) > 0);

  let effectiveStatusColor: string;
  let effectiveStatusLabel: string;
  if (!hasData) {
    effectiveStatusColor = isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400';
    effectiveStatusLabel = 'Missing';
  } else if (statusColor) {
    effectiveStatusColor = statusColor;
    effectiveStatusLabel = healthLabel ?? 'Good';
  } else if (droppedHigh) {
    effectiveStatusColor = 'cr-bg-orange-500';
    effectiveStatusLabel = `Poor ANT reception (${(dropFrac! * 100).toFixed(0)}% dropped)`;
  } else {
    effectiveStatusColor = 'cr-bg-green-500';
    effectiveStatusLabel = 'Good';
  }
  const summaryText = hasData ? antIdSummary ?? undefined : undefined;

  return (
    <ExpandableCard
      title={title}
      statusColor={effectiveStatusColor}
      statusLabel={effectiveStatusLabel}
      summary={summaryText}
      detail={roleDetail(role, showPrimary)}
    >
      <div className="cr-space-y-1">
        <div>Packets: {sensor.raw?.n_samples ?? '--'}</div>
        <div>Sequential: {sensor.packet_count_sequential ? 'Yes' : 'No'}</div>
        <div>Dropped: {sensor.packets_dropped}</div>
        <div>Dropped fraction: {formatPercent(sensor.packets_dropped_fraction)}</div>
        <div>Collisions: {sensor.packets_collision}</div>
        {sensor.raw?.duration_s != null && <div>Time: {formatTime(sensor.raw.duration_s)}</div>}
        <SensorValueStats stats={sensor.raw} />
        {children}
        <SensorInfoSection sensorInfo={sensorInfo} showAntId={false} />
      </div>
    </ExpandableCard>
  );
}

const GPS_ACCURACY_WARNING_M = 6;
const GPS_ACCURACY_ERROR_M = 15;
const GPS_MOVING_FIX_HZ_MIN = 0.25;

function GpsSensorCard({ gps, isOptional = false }: { gps: GpsHealth; isOptional?: boolean }) {
  const hasData = gps.has_data ?? ((gps.raw?.n_samples ?? 0) > 0);
  // Median preferred; older payloads only carry the mean.
  const accuracyMedian = gps.accuracy_median ?? gps.raw?.mean?.accuracy ?? null;
  const movingFixHz = gps.moving_fix_hz ?? null;
  let statusColor: string;
  let statusLabel: string;
  if (!hasData) {
    statusColor = isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400';
    statusLabel = 'Missing';
  } else if (movingFixHz != null && movingFixHz < GPS_MOVING_FIX_HZ_MIN) {
    statusColor = 'cr-bg-red-500';
    statusLabel = `Sparse fixes (${formatNumber(movingFixHz, 2)}Hz while moving < ${GPS_MOVING_FIX_HZ_MIN}Hz)`;
  } else if (accuracyMedian != null && accuracyMedian > GPS_ACCURACY_ERROR_M) {
    statusColor = 'cr-bg-red-500';
    statusLabel = `Poor accuracy (median ${formatNumber(accuracyMedian)}m > ${GPS_ACCURACY_ERROR_M}m)`;
  } else if (accuracyMedian != null && accuracyMedian > GPS_ACCURACY_WARNING_M) {
    statusColor = 'cr-bg-orange-500';
    statusLabel = `Reduced accuracy (median ${formatNumber(accuracyMedian)}m > ${GPS_ACCURACY_WARNING_M}m)`;
  } else {
    statusColor = 'cr-bg-green-500';
    statusLabel = 'Good';
  }
  const summary = hasData ? `Acc median: ${formatNumber(accuracyMedian)}m` : undefined;
  return (
    <ExpandableCard
      title="GPS"
      statusColor={statusColor}
      statusLabel={statusLabel}
      summary={summary}
    >
      <div className="cr-space-y-1">
        <div>Accuracy median: {formatNumber(accuracyMedian)}m</div>
        <div>Accuracy P90: {formatNumber(gps.accuracy_p90)}m</div>
        <div>Packets: {gps.raw?.n_samples ?? '--'}</div>
        <div>Duration: {formatTime(gps.raw?.duration_s ?? null)}</div>
        {gps.moving_duration_s != null && (
          <>
            <div>
              Moving fix rate: {formatNumber(movingFixHz, 2)}Hz
              ({gps.moving_fix_count ?? '--'} fixes / {formatTime(gps.moving_duration_s)})
            </div>
            <div className="cr-text-gray-400">Moving mask: {gps.moving_source ?? '--'}</div>
          </>
        )}
        {gps.pct_under_4m != null && (
          <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
            <div className="cr-font-semibold cr-mb-1">Accuracy Distribution</div>
            <div>&lt;4m: {formatPercent(gps.pct_under_4m)}</div>
            <div>4–8m: {formatPercent(gps.pct_4_to_8m)}</div>
            <div>8–20m: {formatPercent(gps.pct_8_to_20m)}</div>
            <div>&gt;20m: {formatPercent(gps.pct_over_20m)}</div>
          </div>
        )}
        <SensorValueStats stats={gps.raw} />
      </div>
    </ExpandableCard>
  );
}

// Renders the per-channel min/max/mean dict from a SensorChannelStats
// block. Empty stats render nothing.
function SensorValueStats({ stats }: { stats: ImuSensorHealth['raw'] | undefined }) {
  if (!stats) return null;
  const keys = Object.keys(stats.mean ?? {});
  if (keys.length === 0) return null;
  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-1">Channel Stats</div>
      {keys.map(k => (
        <div key={k}>
          {k}: min {formatNumber(stats.min[k])} /
          mean {formatNumber(stats.mean[k])} /
          max {formatNumber(stats.max[k])}
        </div>
      ))}
    </div>
  );
}

function ImuSensorCard({
  title, imu, isOptional = false,
}: {
  title: string;
  imu: ImuSensorHealth | undefined;
  isOptional?: boolean;
}) {
  if (!imu) {
    return (
      <ExpandableCard
        title={title}
        statusColor={isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400'}
        statusLabel="Missing"
      >
        <div>No data</div>
      </ExpandableCard>
    );
  }
  // Prefer the raw sample count; fall back to the gridded count so the card
  // stays truthful even when only the gridded health pass populated samples.
  const displaySamples = imu.raw.n_samples > 0 ? imu.raw.n_samples : imu.gridded?.n_samples ?? 0;
  const hasData = imu.has_data ?? (displaySamples > 0);
  const statusColor = hasData
    ? 'cr-bg-green-500'
    : isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400';
  const statusLabel = hasData ? 'Good' : 'Missing';
  const summary = hasData
    ? `${displaySamples} samples @ ${formatNumber(imu.raw.sample_rate_hz)}Hz`
    : undefined;
  return (
    <ExpandableCard
      title={title}
      statusColor={statusColor}
      statusLabel={statusLabel}
      summary={summary}
    >
      <div className="cr-space-y-1">
        <div>Samples: {displaySamples}</div>
        <div>Duration: {formatTime(imu.raw.duration_s)}</div>
        <div>Sample rate: {formatNumber(imu.raw.sample_rate_hz)} Hz</div>
        <div>dt std: {formatNumber(imu.raw.dt_std_s)} s</div>
        <SensorValueStats stats={imu.raw} />
      </div>
    </ExpandableCard>
  );
}

function GatesSensorCard({ gates, healthObj, isOptional = false }: {
  gates: GateHealth;
  healthObj: RunHealthObj;
  isOptional?: boolean;
}) {
  // Support both new merged format and old separate gate_intersection
  const intersects = gates.route_and_gates_intersect ?? healthObj.gate_intersection?.route_and_gates_intersect ?? null;
  const definedCount = gates.gates_defined_count ?? healthObj.gate_intersection?.gates_defined_count ?? 0;
  const crossedCount = gates.gates_crossed_count ?? healthObj.gate_intersection?.gates_crossed_count ?? 0;

  const statusColor = gates.present
    ? 'cr-bg-green-500'
    : isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400';
  const statusLabel = gates.present ? 'Good' : 'Missing';
  const summary = gates.present ? `${crossedCount}/${definedCount} gates crossed` : undefined;

  return (
    <ExpandableCard
      title="Gates"
      statusColor={statusColor}
      statusLabel={statusLabel}
      summary={summary}
    >
      <div className="cr-space-y-1">
        {gates.gate_a_lat != null && (
          <div>
            Gate A: {formatNumber(gates.gate_a_lat, 5)}, {formatNumber(gates.gate_a_lon, 5)}
          </div>
        )}
        {gates.gate_b_lat != null && (
          <div>
            Gate B: {formatNumber(gates.gate_b_lat, 5)}, {formatNumber(gates.gate_b_lon, 5)}
          </div>
        )}
        {gates.gates_lat && gates.gates_lon && (
          <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
            <div className="cr-font-semibold cr-mb-1">Session Gates ({gates.gates_lat.length})</div>
            {gates.gates_lat.map((lat, i) => (
              <div key={i}>
                {i}: {formatNumber(lat, 5)}, {formatNumber(gates.gates_lon?.[i], 5)}
              </div>
            ))}
          </div>
        )}
        <div className="cr-flex cr-items-center cr-gap-2 cr-mt-1">
          {presenceDot(intersects === true)}
          <span>{intersects ? 'Intersects' : 'No intersection'}</span>
        </div>
      </div>
    </ExpandableCard>
  );
}

function RouteSensorCard({ route, gatesCrossedCount, isOptional = false }: {
  route: RouteHealth;
  gatesCrossedCount: number | null;
  isOptional?: boolean;
}) {
  const lengthKm = route.route_length_m != null ? (route.route_length_m / 1000).toFixed(2) : '--';
  const noGatesCrossed = gatesCrossedCount === 0;
  let statusColor: string;
  let statusLabel: string;
  if (noGatesCrossed) {
    statusColor = 'cr-bg-red-500';
    statusLabel = 'No gates crossed';
  } else if (route.present) {
    statusColor = 'cr-bg-green-500';
    statusLabel = 'Good';
  } else {
    statusColor = isOptional ? 'cr-bg-gray-400' : 'cr-bg-red-400';
    statusLabel = 'Missing';
  }
  const summary = route.present
    ? `${lengthKm} km, ${route.bearing_deg != null ? `${route.bearing_deg}°` : '--'}`
    : undefined;
  return (
    <ExpandableCard
      title="Route"
      statusColor={statusColor}
      statusLabel={statusLabel}
      summary={summary}
    >
      <div className="cr-space-y-1">
        <div>Length: {lengthKm} km</div>
        <div>Bearing: {route.bearing_deg != null ? `${route.bearing_deg}°` : '--'}</div>
        <div>Segments: {route.segment_count ?? '--'}</div>
        {route.start_lat != null && (
          <div>
            Start: {formatNumber(route.start_lat, 5)}, {formatNumber(route.start_lon, 5)}
          </div>
        )}
        {route.end_lat != null && (
          <div>
            End: {formatNumber(route.end_lat, 5)}, {formatNumber(route.end_lon, 5)}
          </div>
        )}
      </div>
    </ExpandableCard>
  );
}



function DiffChannelRow({ label, ch }: { label: string; ch: DiffChannelHealth }) {
  if (ch.status === DiffSensorStatus.UNKNOWN) {
    return (
      <div className="cr-mb-3">
        <span className="cr-font-medium">{label}:</span>{' '}
        <span className="cr-text-gray-400">Unknown</span>
      </div>
    );
  }

  const maxStd = Math.max(ch.start_std_pa ?? 0, ch.end_std_pa ?? 0);
  const isStable = maxStd < 1.0;
  const ref = ch.reference_pa;

  // Assess autocal quality independently of measurement stability
  let autocalGood = true;
  if (ref != null && ch.start_median_pa != null && ch.end_median_pa != null) {
    autocalGood =
      Math.abs(ch.start_median_pa - ref) <= 1.0 && Math.abs(ch.end_median_pa - ref) <= 1.0;
  }

  // Circle color and status text
  let circleColor: string;
  let statusText: string;

  if (ch.status === DiffSensorStatus.ERROR) {
    circleColor = 'cr-bg-red-500';
    statusText = 'Valid Measurement, Measurement Drift';
  } else if (!isStable) {
    circleColor = 'cr-bg-gray-400';
    statusText = `Unstable Measurement, ${autocalGood ? 'Good' : 'Needs'} Autocal`;
  } else if (!autocalGood) {
    circleColor = 'cr-bg-orange-500';
    statusText = 'Valid Measurement, Needs Autocal';
  } else {
    circleColor = 'cr-bg-green-500';
    statusText = 'Valid Measurement, Good Autocal';
  }

  return (
    <div className="cr-mb-3">
      <div>
        <span className="cr-font-medium">{label}:</span>{' '}
        Measurement (σ: {formatNumber(maxStd, 2)}, {isStable ? 'good' : 'unstable'}{' '}
        {isStable ? '<' : '>'} 1 Pa)
      </div>
      <div className="cr-pl-14">
        Zero Ref (ref {ref != null ? formatNumber(ref, 2) : '--'}, start{' '}
        {formatNumber(ch.start_median_pa)} → end {formatNumber(ch.end_median_pa)} Pa)
      </div>
      <div className="cr-pl-14 cr-flex cr-items-center cr-gap-1.5">
        <span className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${circleColor}`} />
        <span>{statusText}</span>
      </div>
    </div>
  );
}

function ProbeHealthRow({ probe }: { probe: NonNullable<WindSensorHealth['probe_health']> }) {
  const mean = probe.mean_abs_dev;
  const status = probe.status;
  const color = healthStatusColor(status);
  const label =
    status === RunHealthStatus.GOOD
      ? 'Good'
      : status === RunHealthStatus.WARNING
        ? 'Elevated — probe may be partially blocked'
        : status === RunHealthStatus.ERROR
          ? 'High — probe likely blocked'
          : 'Unknown';

  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-1">Probe Health</div>
      <div>Mean |Px2PsDev|: {mean != null ? formatNumber(mean, 3) : '--'}</div>
      <div className="cr-text-gray-400">
        Samples: {probe.n_samples ?? '--'} (DynPres &gt; 10 Pa)
      </div>
      <div className="cr-flex cr-items-center cr-gap-1.5 cr-mt-1">
        <span className={`cr-inline-block cr-h-2.5 cr-w-2.5 cr-rounded-full ${color}`} />
        <span>{label}</span>
      </div>
    </div>
  );
}

// --- Sensor Zero Override Panel ---

type ChannelMode = 'auto' | 'manual';

interface ChannelOverrideState {
  checked: boolean;
  mode: ChannelMode;
  manualValue: string;
}

function getSessionRunIds(
  sessionData: any,
  anchorRunTs: string,
  scope: SensorOffsetsScope,
): string[] {
  if (!sessionData?.configs) return [];
  const allRuns: any[] = [];
  for (const config of sessionData.configs) {
    if (config.runs) {
      for (const r of config.runs) {
        allRuns.push(r);
      }
    }
  }
  // Un-stamped runs (year-9999 creation sentinel) have no position in time, so
  // they can be neither ordered against the anchor nor used as one — including
  // them would silently write the override to the wrong set of runs.
  const timestamped = allRuns.filter((r) => !isSentinelRunTs(r.run_ts));
  const anchorTs = new Date(anchorRunTs).getTime();
  switch (scope) {
    case 'this':
      return [];
    case 'after':
      if (isSentinelRunTs(anchorRunTs)) return [];
      return timestamped
        .filter((r) => new Date(r.run_ts).getTime() >= anchorTs)
        .map((r) => r.id);
    case 'before':
      if (isSentinelRunTs(anchorRunTs)) return [];
      return timestamped
        .filter((r) => new Date(r.run_ts).getTime() <= anchorTs)
        .map((r) => r.id);
    case 'all':
      return allRuns.map((r) => r.id);
  }
}

function SensorZeroOverridePanel({
  wsh,
  run,
  sessionData,
  onSaved,
}: {
  wsh: WindSensorHealth;
  run: any;
  sessionData?: any;
  onSaved: () => void;
}) {
  const channels: [string, DiffChannelHealth, keyof WindSensor5ZeroOverride][] = [
    ['Diff 1', wsh.diff1, 'diff1_zero_pa'],
    ['Diff 2', wsh.diff2, 'diff2_zero_pa'],
    ['Diff 3', wsh.diff3, 'diff3_zero_pa'],
  ];
  const needsAutocalChannels = channels.filter(
    ([, ch]) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
  );

  const [channelStates, setChannelStates] = useState<Record<string, ChannelOverrideState>>(
    () => {
      const init: Record<string, ChannelOverrideState> = {};
      for (const [, , key] of needsAutocalChannels) {
        init[key] = { checked: true, mode: 'auto', manualValue: '' };
      }
      return init;
    },
  );
  const [saving, setSaving] = useState(false);

  const updateChannel = useCallback(
    (key: string, patch: Partial<ChannelOverrideState>) => {
      setChannelStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
    },
    [],
  );

  const anyChecked = Object.values(channelStates).some((s) => s.checked);
  const hasSession = !!sessionData?.configs;

  const buildOverride = (): WindSensor5ZeroOverride => {
    const ov: WindSensor5ZeroOverride = { source: 'health_check_start_median' };
    for (const [, ch, key] of needsAutocalChannels) {
      const st = channelStates[key];
      if (!st?.checked) continue;
      if (st.mode === 'auto') {
        (ov as any)[key] = ch.start_median_pa;
      } else {
        const val = parseFloat(st.manualValue);
        if (!isFinite(val)) continue;
        (ov as any)[key] = val;
        ov.source = 'manual';
      }
    }
    return ov;
  };

  const handleApply = async (scope: SensorOffsetsScope) => {
    const override = buildOverride();
    const hasValues = needsAutocalChannels.some(([, , key]) => {
      return (override as any)[key] != null;
    });
    if (!hasValues) {
      toast.error('No valid values to apply');
      return;
    }

    const sensorOffsets = {
      version: 1,
      wind_sensor_5: override,
    };

    if (scope === 'this') {
      setSaving(true);
      try {
        const existing = run.sensor_offsets || {};
        const merged = {
          ...existing,
          ...sensorOffsets,
          wind_sensor_5: {
            ...(existing.wind_sensor_5 || {}),
            ...override,
          },
        };
        await patchRun(run.id, { sensor_offsets: merged });
        toast.success('Sensor zero override saved');
        onSaved();
      } catch {
        toast.error('Failed to save sensor zero override');
      } finally {
        setSaving(false);
      }
      return;
    }

    const targetIds = getSessionRunIds(sessionData, run.run_ts, scope);
    if (targetIds.length === 0) {
      toast.error('No runs found for the selected scope');
      return;
    }

    const scopeLabel = scope === 'after' ? 'this and later'
      : scope === 'before' ? 'this and earlier' : 'all';
    const ok = window.confirm(
      `Apply zero override to ${targetIds.length} run(s) `
      + `(${scopeLabel} in this session)?`,
    );
    if (!ok) return;

    setSaving(true);
    try {
      const bodies = targetIds.map((id) => ({
        id,
        sensor_offsets: sensorOffsets,
      }));
      await patchRunsBulk(bodies);
      toast.success(`Sensor zero override applied to ${targetIds.length} run(s)`);
      onSaved();
    } catch {
      toast.error('Failed to apply bulk sensor zero override');
    } finally {
      setSaving(false);
    }
  };

  if (needsAutocalChannels.length === 0) return null;

  return (
    <div className="cr-mt-3 cr-border-t cr-border-dashed cr-pt-3">
      <div className="cr-font-semibold cr-mb-2">Apply New Zero</div>
      {needsAutocalChannels.map(([label, ch, key]) => {
        const st = channelStates[key];
        if (!st) return null;
        return (
          <div key={key} className="cr-mb-2 cr-flex cr-items-center cr-gap-2">
            <input
              type="checkbox"
              checked={st.checked}
              onChange={(e) => updateChannel(key, { checked: e.target.checked })}
            />
            <span className="cr-font-medium cr-w-12">{label}</span>
            <select
              className="cr-text-xs cr-border cr-rounded cr-px-1 cr-py-0.5"
              value={st.mode}
              onChange={(e) => updateChannel(key, {
                mode: e.target.value as ChannelMode,
              })}
            >
              <option value="auto">Auto</option>
              <option value="manual">Manual</option>
            </select>
            {st.mode === 'auto' ? (
              <span className="cr-text-xs cr-text-gray-500">
                {formatNumber(ch.start_median_pa, 2)} Pa
              </span>
            ) : (
              <input
                type="number"
                step="0.01"
                className="cr-text-xs cr-border cr-rounded cr-px-1 cr-py-0.5 cr-w-20"
                placeholder="Pa"
                value={st.manualValue}
                onChange={(e) => updateChannel(key, { manualValue: e.target.value })}
              />
            )}
            <span className="cr-text-xs cr-text-gray-400">
              (ref: {formatNumber(ch.reference_pa, 2)} Pa)
            </span>
          </div>
        );
      })}
      <div className="cr-flex cr-flex-wrap cr-gap-2 cr-mt-2">
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-600
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || saving}
          onClick={() => handleApply('this')}
        >
          Update this run
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('after')}
        >
          Update &amp; runs after
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('before')}
        >
          Update &amp; runs before
        </button>
        <button
          className="cr-text-xs cr-px-2 cr-py-1 cr-rounded cr-bg-blue-500
            cr-text-white disabled:cr-opacity-40"
          disabled={!anyChecked || !hasSession || saving}
          onClick={() => handleApply('all')}
        >
          Update all runs
        </button>
      </div>
      <div className="cr-text-xs cr-text-gray-400 cr-mt-1">
        Reprocess to apply the new zero values.
      </div>
    </div>
  );
}

function WindSensorHealthSection({
  wsh,
  run,
  sessionData,
  onSaved,
}: {
  wsh: WindSensorHealth;
  run?: any;
  sessionData?: any;
  onSaved?: () => void;
}) {
  const channels = [wsh.diff1, wsh.diff2, wsh.diff3];
  const hasUnstable = channels.some((ch) => ch.status === DiffSensorStatus.UNSTABLE);
  const hasNeedsAutocal = channels.some(
    (ch) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
  );

  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-2">Zero Comparison</div>
      <DiffChannelRow label="Diff 1" ch={wsh.diff1} />
      <DiffChannelRow label="Diff 2" ch={wsh.diff2} />
      <DiffChannelRow label="Diff 3" ch={wsh.diff3} />
      {hasNeedsAutocal && (
        <div className="cr-mt-1 cr-text-orange-600 cr-text-xs">
          Rezero recommended — one or more channels have drifted from the reference.
        </div>
      )}
      {hasNeedsAutocal && run && onSaved && (
        <SensorZeroOverridePanel
          wsh={wsh}
          run={run}
          sessionData={sessionData}
          onSaved={onSaved}
        />
      )}
      {hasUnstable && (
        <div className="cr-mt-1 cr-text-gray-500 cr-text-xs cr-italic">
          Measurements are taken in first and last 5s of sampling. Stable measurements
          may not be possible on windy days without shielding.
        </div>
      )}
      {wsh.probe_health && <ProbeHealthRow probe={wsh.probe_health} />}
    </div>
  );
}


// --- Fallback for old format ---

function FallbackHealthDisplay({ runHealth }: { runHealth: number | null | undefined }) {
  const label =
    runHealth === RunHealthStatus.GOOD
      ? 'Good'
      : runHealth === RunHealthStatus.WARNING
        ? 'Warning'
        : runHealth === RunHealthStatus.ERROR
          ? 'Error'
          : runHealth === RunHealthStatus.AVERAGE
            ? 'Average'
            : runHealth != null
              ? `Status ${runHealth}`
              : 'No health data';

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <div className="cr-flex cr-items-center cr-gap-3">
        <span className={`cr-inline-block cr-h-4 cr-w-4 cr-rounded-full ${healthStatusColor(runHealth)}`} />
        <span className={`cr-text-lg cr-font-bold ${healthStatusTextColor(runHealth)}`}>{label}</span>
      </div>
      <p className="cr-mt-2 cr-text-sm cr-text-gray-400">Detailed health data is not available for this run.</p>
    </div>
  );
}

// --- Main Component ---

export default function RunHealthCheck() {
  const { run, calibration, sessionData } = useOutletContext<RunHealthCheckContext>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Second entry point to Lap Parsing (the ⋮ actions menu is the other one).
  // The nested lap-parsing sub-route is reachable for any session type, even
  // when its tab chip is hidden.
  const goToLapParsing = useCallback(() => {
    const pathParts = location.pathname.split('/');
    const runIndex = pathParts.findIndex((p) => p === 'runs');
    const basePath = pathParts.slice(0, runIndex + 2).join('/');
    navigate(`${basePath}/lap-parsing`);
  }, [location.pathname, navigate]);

  const invalidateRun = useCallback(() => {
    if (run?.id) {
      queryClient.invalidateQueries({
        queryKey: ['runAndCalibration', run.id],
      });
    }
  }, [queryClient, run?.id]);

  const [antExpanded, setAntExpanded] = useState(false);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const [savingForceSynthDp, setSavingForceSynthDp] = useState(false);

  const saveForceSyntheticDp = useCallback(async (next: boolean | null) => {
    if (!run?.id) return;
    setSavingForceSynthDp(true);
    try {
      await patchRun(run.id, {
        parser_vars: withForceSyntheticDp(run.parser_vars, next),
      });
      toast.success('Synthetic DP setting saved — reprocess to apply');
      invalidateRun();
    } catch {
      toast.error('Failed to save synthetic DP setting');
    } finally {
      setSavingForceSynthDp(false);
    }
  }, [run, invalidateRun]);

  // Primary/position per card. The pipeline stamps both onto sensor_info;
  // runs processed before it did fall back to the same derivation done here
  // from the run's own records (see sensorRoles.ts).
  //
  // Declared with the other hooks, above the two early returns below: this
  // component renders once with no `run` and again with a legacy health
  // object, so a useMemo placed after those returns changes the hook count
  // between renders (React error #310).
  const derivedRoles = useMemo(() => sensorRoles(run, calibration), [run, calibration]);

  if (!run) {
    return <div className="cr-text-gray-400 cr-p-4">Loading...</div>;
  }

  const healthObj = run.run_health_obj as RunHealthObj | null | undefined;

  // Detect new-format health object by presence of health_status_name
  const isNewFormat = healthObj && 'health_status_name' in healthObj;

  if (!isNewFormat) {
    return <FallbackHealthDisplay runHealth={run.run_health} />;
  }

  const sensors = healthObj.sensors_present;
  // Support both new 'wind' key and legacy 'aero' key
  const windSensor = sensors.wind ?? sensors.aero;
  const sensorInfo = healthObj.sensor_info;
  const optionalSet = new Set(healthObj.missing_optional_sensors ?? []);
  const isOptional = (name: string) => optionalSet.has(name);

  const cardRole = (key: string): SensorRole =>
    roleFor(sensorInfo?.[key], sensorInfo?.[key]?.ant_device_number, derivedRoles);
  // "More than one" — the test for whether naming a primary tells you anything.
  const manyPower = Boolean(sensors.power_meter_2 || sensors.power_meter_3);
  const manySpeed = Boolean(sensors.speed_2 || sensors.speed_3);
  // Speed cards are titled by IDENTITY (position + id), never the logging
  // instance the card key derives from — that ordinal is diagnostic, shown
  // as a small line inside the card instead. Ordinal fallback only when
  // the run recorded no id. (Power/cadence keep ordinal titles until their
  // functions migrate off instances.)
  const speedCardTitle = (key: string, fallback: string): string => {
    const id = sensorInfo?.[key]?.ant_device_number;
    if (id == null) return fallback;
    return `Speed — ${sensorIdentity(id, cardRole(key).position)}`;
  };
  const logStreamLine = (key: string) => (
    <div className="cr-text-gray-400">Log stream: {key} (diagnostic)</div>
  );

  return (
    <div className="cr-space-y-6">
      {/* Overall Status — width snaps down to the rightmost sensor card so the
          bar lines up with the card grid instead of running full-screen. */}
      <div
        className="cr-w-full cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md
          md:cr-w-[calc(round(down,100%_+_1rem,15rem)_-_1rem)]"
      >
        <HealthStatusBadge healthObj={healthObj} dp={run.solve?.selections?.dynamic_pressure} />
        {healthObj.health_status !== RunHealthStatus.GOOD && (
          (healthObj.health_status_reasons?.length ?? 0) > 0 ? (
            <ul className="cr-mt-1 cr-mb-2 cr-text-sm cr-text-gray-700 cr-list-disc cr-list-inside">
              {healthObj.health_status_reasons!.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p className="cr-mt-1 cr-mb-2 cr-text-sm cr-italic cr-text-gray-500">
              No specific reasons recorded. This run was processed by a legacy pipeline;
              re-process to populate the cause list.
            </p>
          )
        )}
        {run.health_code != null && (
          <p className="cr-text-sm cr-text-gray-300">
            Pipeline: {getStageName(run.health_code)}
            {run.health_code < ProcessingStage.COMPLETE && ' (incomplete)'}
          </p>
        )}
        {healthObj.total_run_time_seconds != null && (
          <div className="cr-mt-1 cr-text-sm cr-text-blue-900">
            Run time: {formatTime(healthObj.total_run_time_seconds)}
          </div>
        )}
        <div className="cr-mt-3 cr-border-t cr-border-dashed cr-pt-3">
          <ForceSyntheticDpControl
            scope="run"
            value={run.parser_vars?.atmosphere?.force_synthetic_dp}
            sessionValue={sessionData?.parser_vars?.atmosphere?.force_synthetic_dp}
            saving={savingForceSynthDp}
            onSave={saveForceSyntheticDp}
          />
        </div>
      </div>

      {/* ANT Sensor Cards */}
      <div>
        <SectionHeader title="ANT Sensors" expanded={antExpanded} onToggle={() => setAntExpanded((v) => !v)} />
        <SectionExpandedContext.Provider value={antExpanded}>
        <div className="cr-flex cr-flex-wrap cr-items-start cr-gap-4">
          <AntSensorCard
            title="Power Meter"
            sensor={sensors.power_meter}
            sensorInfo={sensorInfo?.power_meter}
            role={cardRole('power_meter')}
            showPrimary={manyPower}
          />
          {windSensor && (() => {
            const wsh = healthObj.wind_sensor_health;
            const needsAutocal = wsh && [wsh.diff1, wsh.diff2, wsh.diff3].some(
              (ch) => ch.status === DiffSensorStatus.NEEDS_AUTOCAL,
            );
            const probeStatus = wsh?.probe_health?.status;
            const probeBad = probeStatus === RunHealthStatus.ERROR;
            const probeWarn = probeStatus === RunHealthStatus.WARNING;
            const statusColor = probeBad
              ? 'cr-bg-red-500'
              : (needsAutocal || probeWarn)
                ? 'cr-bg-orange-500'
                : undefined;
            const healthLabel = probeBad
              ? 'Probe blockage'
              : probeWarn
                ? 'Probe partially blocked'
                : needsAutocal
                  ? 'Rezero recommended'
                  : undefined;
            return (
              <AntSensorCard
                title="Wind"
                sensor={windSensor}
                sensorInfo={sensorInfo?.wind}
                role={cardRole('wind')}
                statusColor={statusColor}
                healthLabel={healthLabel}
              >
                {wsh && (
                  <WindSensorHealthSection
                    wsh={wsh}
                    run={run}
                    sessionData={sessionData}
                    onSaved={invalidateRun}
                  />
                )}
              </AntSensorCard>
            );
          })()}
          <AntSensorCard
            title={speedCardTitle('speed', 'Speed')}
            sensor={sensors.speed}
            sensorInfo={sensorInfo?.speed}
            role={cardRole('speed')}
            showPrimary={manySpeed}
          >
            {manySpeed && logStreamLine('speed')}
            {healthObj.speed_health && (
              <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2 cr-space-y-1">
                <div className="cr-font-semibold cr-mb-1">Speed Health</div>
                <div>
                  Wheel skips: {healthObj.speed_health.wheel_skip_count ?? '--'}
                  {healthObj.speed_health.wheel_skip_ratio != null &&
                    ` (${formatPercent(healthObj.speed_health.wheel_skip_ratio)})`}
                </div>
                <div>
                  GPS/Wheel CRC in tolerance: {formatPercent(healthObj.speed_health.gps_wheel_crc_in_tolerance)}
                  <span className="cr-text-gray-400 cr-ml-1">
                    (tol: {formatPercent(healthObj.speed_health.tolerance)})
                  </span>
                </div>
              </div>
            )}
          </AntSensorCard>
          {sensors.power_meter_2 && (
            <AntSensorCard
              title="Power Meter 2"
              sensor={sensors.power_meter_2}
              sensorInfo={sensorInfo?.power_meter_2}
              role={cardRole('power_meter_2')}
              showPrimary={manyPower}
            />
          )}
          {sensors.power_meter_3 && (
            <AntSensorCard
              title="Power Meter 3"
              sensor={sensors.power_meter_3}
              sensorInfo={sensorInfo?.power_meter_3}
              role={cardRole('power_meter_3')}
              showPrimary={manyPower}
            />
          )}
          {sensors.speed_2 && (
            <AntSensorCard
              title={speedCardTitle('speed_2', 'Speed 2')}
              sensor={sensors.speed_2}
              sensorInfo={sensorInfo?.speed_2}
              role={cardRole('speed_2')}
              showPrimary={manySpeed}
            >
              {logStreamLine('speed_2')}
            </AntSensorCard>
          )}
          {sensors.speed_3 && (
            <AntSensorCard
              title={speedCardTitle('speed_3', 'Speed 3')}
              sensor={sensors.speed_3}
              sensorInfo={sensorInfo?.speed_3}
              role={cardRole('speed_3')}
              showPrimary={manySpeed}
            >
              {logStreamLine('speed_3')}
            </AntSensorCard>
          )}
          {sensors.body && (
            <AntSensorCard
              title="Body"
              sensor={sensors.body}
              sensorInfo={sensorInfo?.body}
              isOptional
            >
              {(sensors.body.ref_chest_cm != null
                || sensors.body.ref_head_cm != null
                || sensors.body.ref_waist_cm != null) && (
                <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
                  <div className="cr-font-semibold cr-mb-1">Body Refs</div>
                  {sensors.body.ref_chest_cm != null && <div>Chest: {sensors.body.ref_chest_cm.toFixed(1)} cm</div>}
                  {sensors.body.ref_head_cm != null && <div>Head: {sensors.body.ref_head_cm.toFixed(1)} cm</div>}
                  {sensors.body.ref_waist_cm != null && <div>Waist: {sensors.body.ref_waist_cm.toFixed(1)} cm</div>}
                </div>
              )}
            </AntSensorCard>
          )}
        </div>
        </SectionExpandedContext.Provider>
      </div>

      {/* Non-radio Sensors */}
      <div>
        <SectionHeader title="Other Sensors" expanded={otherExpanded} onToggle={() => setOtherExpanded((v) => !v)} />
        <SectionExpandedContext.Provider value={otherExpanded}>
        <div className="cr-flex cr-flex-wrap cr-items-start cr-gap-4">
          <GpsSensorCard gps={sensors.gps} isOptional={isOptional('gps')} />
          <ImuSensorCard title="Accelerometer" imu={sensors.accel} isOptional={isOptional('accel')} />
          <ImuSensorCard title="Gyroscope" imu={sensors.gyro} isOptional={isOptional('gyro')} />
          <ImuSensorCard title="Magnetometer" imu={sensors.mag} isOptional={isOptional('mag')} />
          <GatesSensorCard gates={sensors.gates} healthObj={healthObj} isOptional={isOptional('gates')} />
          <RouteSensorCard
            route={sensors.route}
            gatesCrossedCount={
              sensors.gates?.gates_crossed_count
              ?? healthObj.gate_intersection?.gates_crossed_count
              ?? null
            }
            isOptional={isOptional('route')}
          />
        </div>
        </SectionExpandedContext.Provider>
      </div>

      {/* Secondary entry point to the Lap Parsing screen */}
      <div className="cr-flex cr-justify-start cr-border-t cr-pt-4">
        <button
          type="button"
          onClick={goToLapParsing}
          className="cr-flex cr-items-center cr-gap-2 cr-rounded-md cr-bg-blue-600 cr-px-4 cr-py-2
            cr-text-sm cr-font-medium cr-text-white hover:cr-bg-blue-700"
        >
          <HiOutlineFlag className="cr-h-5 cr-w-5" />
          <span>Lap Parsing</span>
        </button>
      </div>

    </div>
  );
}
